<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div class="header-extra">
        <ColumnToggle
          :table-name="'authConnections'"
          :user-settings="userSettings"
          :settings-status="getUserSettingsStatus"
          :column-settings="columnSettings"
          @onChange="updateUserVisibleColumns"
        />
        <input
          v-model="filterValue"
          type="search"
          :placeholder="$t('SEARCH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!loading"
      class="m-b-15"
    >
      <button
        type="button"
        class="btn btn-success btn-sm"
        :disabled="showCreateForm"
        @click="toggleForm(true)"
      >
        <i class="fa fa-plus" />
        <span v-t="'ACTION_CREATE_AUTH_CONNECTION'" />
      </button>
    </div>
    <div
      v-if="!loading"
      class="grid_table_holder"
    >
      <v-table
        :data="authConnections"
        :current-page.sync="currentPage"
        :page-size="20"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />
            <v-th
              v-for="col in userVisibleColumns"
              :key="col"
              v-t="columnSettings[col].displayName"
              :class="columnSettings[col].class"
              :default-sort="columnSettings[col].defaultSort"
              :sort-key="columnSettings[col].name"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <tr>
            <td
              v-if="emptyTable && !showCreateForm"
              v-t="'AUTH_CONNECTIONS_TABLE_EMPTY'"
              colspan="100%"
            />
          </tr>
          <tr
            v-if="showCreateForm"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <AuthConnectionsForm
                :show-loader="createAuthConnectionStatus.status === 'pending'"
                :is-new-url-parameter="true"
                @cancel="toggleForm(false)"
                @submit="createAuth"
              />
            </td>
          </tr>
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="row.id"
              :class="{'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 }"
            >
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  :disabled="showCreateForm"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>
              <td
                v-if="userVisibleColumns.includes('name')"
                class="clickable"
                @click="displayEditForm(row.id)"
              >
                {{ row.name }}
              </td>
              <td v-if="userVisibleColumns.includes('url')">
                {{ row.url }}
              </td>
              <td v-if="userVisibleColumns.includes('lastUsedAt')">
                {{ formatDate(row.lastUsedAt) }}
              </td>
              <td
                v-if="userVisibleColumns.includes('status')"
                class="text-center"
              >
                <span
                  v-tooltip.right="getStatusMessage(row)"
                  class="color-dot"
                  :class="{'yellow_status': row.isConnectionHealthy == null, 'green_status': row.isConnectionHealthy, 'red_status': row.isConnectionHealthy === false}"
                />
              </td>
              <td v-if="!isMobile">
                <AuthConnectionsActions
                  :current-auth-connection="row"
                  :disabled="disabled"
                  :get-references-status="getReferencesStatus"
                  :delete-auth-connection-status="deleteAuthConnectionStatus"
                  :references="references"
                  @edit="displayEditForm"
                  @open-delete="getAuthReferences"
                  @confirm-delete="deleteAuth"
                  @validate="validateAuth"
                />
              </td>
            </tr>
            <tr
              v-if="showEditForm(row.id)"
              :key="row.id + '-edit'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <AuthConnectionsForm
                  :show-loader="updateAuthConnectionStatus.status === 'pending'"
                  :current-auth-connection="row"
                  :is-new-url-parameter="false"
                  @cancel="hideEditForm"
                  @submit="updateAuth"
                />
              </td>
            </tr>
            <tr
              v-if="isMobile && activeActionRowId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <AuthConnectionsActions
                  :current-auth-connection="row"
                  :disabled="disabled"
                  :get-references-status="getReferencesStatus"
                  :delete-auth-connection-status="deleteAuthConnectionStatus"
                  :references="references"
                  @collapse-actions="!disabled && hideMobileAction()"
                  @edit="displayEditForm"
                  @open-delete="getAuthReferences"
                  @confirm-delete="deleteAuth"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-table>
      <div class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="20"
          />
        </div>
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'AUTH_CONNECTIONS_TABLE_LOADING'" />
    </div>
  </PortalWidget>
</template>

<script>

import messages from '../locales';
import sharedMessages from '@/core/locales';


import PortalWidget from '@/core/components/PortalWidget.vue';
import AuthConnectionsActions from './AuthConnectionsActions';
import AuthConnectionsForm from './AuthConnectionsForm';
import ColumnToggle from '@/core/components/ColumnToggle/ColumnToggle';
import Loader from '@/core/components/Loader';
import { isMobile } from '@/core/utilities';
import { authConnectionService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import { utcToLocalFormat } from '@/core/filters/utcToLocalFormat';

export default {
  name: 'AuthConnections',
  components: {
    Loader,
    AuthConnectionsForm,
    AuthConnectionsActions,
    PortalWidget,
    ColumnToggle
  },
  props: { },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      currentUrlParameter: { },
      isNewUrlParameter: true,
      showCreateForm: false,
      isMobile: isMobile,
      activeActionRowId: null,
      activeEditRowId: null,
      activeUrlParamReferences: null,
      widget: {
        title:this.$i18n.t('HEADER_AUTH_CONNECTION'),
        iconClass: 'typcn typcn-code'
      },
      filterValue: '',
    };
  },
  computed: {
    ...mapState('authConnections', [
      'authConnections',
      'references',
      'userVisibleColumns',
    ]),
    ...mapState('ui', [
      'userSettings'
    ]),
    ...mapGetters('ui', [
      'getUserSettingsStatus',
    ]),
    ...mapGetters('authConnections', [
      'getAuthConnectionsStatus',
      'createAuthConnectionStatus',
      'updateAuthConnectionStatus',
      'deleteAuthConnectionStatus',
      'validateAuthConnectionStatus',
      'getReferencesStatus'
    ]),
    filters() {
      return {
        name: {
          value: this.filterValue,
          keys: ['name', 'id', 'url'],
        }
      };
    },
    loading() {
      return this.getAuthConnectionsStatus.status == STATUS_PENDING;
    },
    emptyTable() {
      return this.getAuthConnectionsStatus.status == STATUS_SUCCESS
      && this.authConnections.length == 0;
    },
    columnSettings() {
      return authConnectionService.columnSettings;
    },
    disabled() {
      return this.createAuthConnectionStatus.status == STATUS_PENDING
      || this.updateAuthConnectionStatus.status == STATUS_PENDING
      || this.showCreateForm
      || !!this.activeEditRowId;
    }
  },
  watch: {
    currentPage() {
      this.hideEditForm();
      this.showCreateForm = false;
    },
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.hideEditForm();
        this.showCreateForm = false;
      }
    }
  },
  created() {
    this.getAuthConnections();
    this.getUserSettings();
  },
  methods: {
    ...mapActions('authConnections', [
      'getAuthConnections',
      'createAuthConnection',
      'updateAuthConnection',
      'getReferences',
      'deleteAuthConnection',
      'getUserVisibleColumns',
      'validateAuthConnection'
    ]),
    ...mapActions('ui', [
      'getUserSettings'
    ]),
    getStatusMessage(row) {
      if (row.isConnectionHealthy == null) {
        return 'Pending';
      }
      return row.isConnectionHealthy ? 'Ok' : row.latestConnectivityError;
    },
    toggleForm(open) {
      if (open) this.hideEditForm();
      this.showCreateForm = open;
    },
    updateUserVisibleColumns() {
      this.getUserVisibleColumns();
    },
    displayEditForm(id) {
      this.toggleForm(false);
      this.hideEditForm();
      this.activeEditRowId = id;
    },
    hideEditForm() {
      this.activeEditRowId = null;
    },
    showEditForm(rowId) {
      return this.activeEditRowId == rowId;
    },
    displayMobileAction(rowId) {
      this.activeActionRowId = rowId;
    },
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    formatDate(date) {
      if (!date) {
        return 'None';
      } else {
        return utcToLocalFormat(date);
      }
    },
    async getUrlReferences(id) {
      return await this.getReferences(id);
    },
    async getAuthReferences(id) {
      await this.getReferences(id);
    },
    async createAuth(authConnection) {
      await this.createAuthConnection(authConnection);
      if (this.createAuthConnectionStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_CREATE_SUCCESS'));
        this.toggleForm(false);
      }
      if (this.createAuthConnectionStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.createAuthConnectionStatus.error);
      }
    },
    async updateAuth(authConnection) {
      await this.updateAuthConnection(authConnection);
      if (this.updateAuthConnectionStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_UPDATE_SUCCESS'));
        this.hideEditForm();
      }
      if (this.updateAuthConnectionStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.updateAuthConnectionStatus.error);
      }
    },
    async deleteAuth(id) {
      await this.deleteAuthConnection(id);
      if (this.deleteAuthConnectionStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_DELETE_SUCCESS'));
      }
      if (this.deleteAuthConnectionStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.deleteAuthConnectionStatus.error);
      }
    },
    async validateAuth(id) {
      if (!this.activeEditRowId) {
        await this.validateAuthConnection(id);
      }
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
